import { FC, Fragment, useEffect, useState } from 'react'
import {
  Body1,
  Box,
  Button,
  Divider,
  Link,
  makeStyles,
  MuiStack,
  Typography,
} from '@perk-ui/core'
import { FieldArray, FormikProvider, useFormik } from 'formik'
import { useHistory, useRouteMatch } from 'react-router-dom'
import * as yup from 'yup'

import { useCurrentSite } from '../../../components/SitesProvider'
import useBodySites from '../../../features/query-hooks/useBodySites'
import useCreateDiagnosis, {
  PatientDiagnosis,
  PatientDiagnosisSeverity,
} from '../../../features/query-hooks/useCreateDiagnosis'
import usePatientCases, {
  PatientCase,
} from '../../../features/query-hooks/usePatientCases'
import useUpdateDiagnosis from '../../../features/query-hooks/useUpdateDiagnosis'
import DiagnosisForm, {
  DiagnosisFormValues,
} from '../../PatientFlow/Diagnosis/components/DiagnosisForm'
import PatientSelfOnboardUrls, {
  PatientSelfOnboardParams,
} from '../PatientSelfOnboardUrls'

interface DiagnosisFormValuesArr {
  diagnoses: DiagnosisFormValues[]
}

interface DiagnosisProps {}

const useStyles = makeStyles((theme) => ({
  root: {
    justifyContent: 'space-between',
    maxWidth: '787px',
    width: '100%',
    height: '100%',
  },
  form: {
    marginTop: theme.spacing(2),
    '& > *:not(:last-child)': {
      marginBottom: theme.spacing(2),
    },
    '& > .MuiDivider-root': {
      marginBottom: theme.spacing(3),
    },
    '& .MuiAutocomplete-input': {
      maxWidth: '100%',
    },
  },
  bodyPartName: {
    textAlign: 'center',
    fontFamily: 'DM Sans',
    fontSize: '28px',
    lineHeight: '24px',
  },
  goBackButton: {
    border: '1px solid #90CAF9', // Light blue border
    borderRadius: '8px', // Rounded corners
    color: '#42A5F5 !important', // Blue text color
    textColor: '#42A5F5', // Blue text color
    textTransform: 'none', // Prevents all-uppercase text
    // fontWeight: 500, // Semi-bold text
    backgroundColor: 'white', // White background
    '&:hover': {
      backgroundColor: '#E3F2FD', // Light blue background on hover
    },
  },
}))

const diagnosisHasChanged = (
  diag1?: DiagnosisFormValues & Pick<PatientDiagnosis, 'isPrimary'>,
  diag2?: PatientDiagnosis,
) => {
  if (!diag1 || !diag2) return true
  if (diag1.isPrimary !== diag2.isPrimary) return true
  if (diag1.severity !== diag2.severity) return true
  if (diag1.medicalConditionId !== diag2.medicalConditionId) return true
  if (diag1.radiographicClassification !== diag2.radiographicClassification)
    return true
  if (
    diag1.locations.map((l) => l.id).join() !==
    diag2.locations.map((l) => l.id).join()
  )
    return true

  return false
}

const validationSchema = yup.object({
  diagnoses: yup.array().of(
    yup.object({
      medicalConditionId: yup
        .string()
        .defined('A Primary Diagnosis must be selected'),
      severity: yup
        .string()
        .oneOf(['mild', 'moderate', 'severe'])
        .defined('Please select a severity'),
      locations: yup.array().of(
        yup.object({
          id: yup.string(),
        }),
      ),
      radiographicClassification: yup.string().nullable(),
    }),
  ),
})

const Diagnosis: FC<DiagnosisProps> = () => {
  const classes = useStyles()
  const hasDiagnosis = useRouteMatch<PatientSelfOnboardParams>(
    PatientSelfOnboardUrls.addDiagnoses.match,
  )
  const { patientId, caseId } = hasDiagnosis?.params || {}
  const currentSite = useCurrentSite()
  const history = useHistory()

  const createDiagnosis = useCreateDiagnosis()
  const updateDiagnosis = useUpdateDiagnosis()

  const { data: patientCases = [] } = usePatientCases(patientId)
  const [patientCase, setPatientCase] = useState<PatientCase | null>(null)
  const hasImmunologySpecialty = currentSite?.specialty.includes(
    'Rheumatology/Immunology',
  )

  const { data: bodySites = [] } = useBodySites()

  const [existingDiagnoses, setExistingDiagnoses] = useState<
    PatientDiagnosis[]
  >([])

  const bodySiteName = bodySites.find(
    (site) => site.id === patientCase?.bodySiteId,
  )?.name

  useEffect(() => {
    setPatientCase(patientCases.find((pc) => pc.id === caseId) || null)
    setExistingDiagnoses(
      patientCases.find((pc) => pc.id === caseId)?.patientDiagnoses || [],
    )
  }, [patientCases])

  const formik = useFormik<DiagnosisFormValuesArr>({
    enableReinitialize: true,
    validationSchema,
    initialValues: {
      diagnoses:
        existingDiagnoses.length > 0
          ? existingDiagnoses
          : [
              {
                medicalConditionId: '',
                severity: hasImmunologySpecialty ? 'moderate' : '',
                locations: [],
              },
            ],
    },
    // Wait for onSubmit until all diagnoses have been changed
    onSubmit(values) {
      const caseId = patientCase?.id
      if (!patientId || !caseId) return

      const createsAndUpdates = values.diagnoses.map((diagnosis, idx) => {
        if (diagnosis.id) {
          const shouldUpdate = diagnosisHasChanged(
            {
              ...diagnosis,
              isPrimary: idx === 0,
            },
            existingDiagnoses[idx],
          )

          if (shouldUpdate) {
            return updateDiagnosis.mutateAsync({
              ...diagnosis,
              id: diagnosis.id,
              patientCaseId: caseId,
              severity: diagnosis.severity as PatientDiagnosisSeverity,
              isPrimary: idx === 0,
              patientId,
            })
          }
        } else {
          return createDiagnosis.mutateAsync({
            ...diagnosis,
            patientCaseId: caseId,
            severity: diagnosis.severity as PatientDiagnosisSeverity,
            isPrimary: idx === 0,
            patientId,
          })
        }
      })
      return Promise.all(createsAndUpdates).then(() => {
        history.push({
          pathname: PatientSelfOnboardUrls.addTreatment.create(
            patientId,
            caseId,
          ),
          state: { from: location.pathname },
        })
      })
    },
  })
  const {
    setFieldValue,
    handleBlur,
    touched,
    errors,
    values,
    isSubmitting,
    isValid,
    submitForm,
  } = formik

  return (
    <Box className={classes.root}>
      <Typography className={classes.bodyPartName}>
        <b>Diagnosis:</b> {bodySiteName}
      </Typography>
      <form className={classes.form}>
        <FormikProvider value={formik}>
          <FieldArray name="diagnoses" validateOnChange={false}>
            {({ push, remove }) => {
              return (
                <>
                  {values.diagnoses.map((diagnosis, idx) => {
                    return (
                      <Fragment key={idx}>
                        <DiagnosisForm
                          patientCase={patientCase ?? null}
                          diagnosis={diagnosis}
                          bodySiteId={patientCase?.bodySiteId || ''}
                          bodySites={[]}
                          setFieldValue={setFieldValue}
                          handleBlur={handleBlur}
                          touched={touched.diagnoses?.[idx] ?? null}
                          errors={errors.diagnoses?.[idx] ?? null}
                          index={idx}
                          onRemove={async () => {
                            if (diagnosis.id) {
                              //   await deleteDiagnosis.mutateAsync(diagnosis.id)
                            }
                            remove(idx)
                          }}
                        />
                        <Divider />
                      </Fragment>
                    )
                  })}
                  <MuiStack direction="row" justifyContent="space-between">
                    <Link
                      component="button"
                      type="button"
                      underline="hover"
                      onClick={() =>
                        push({
                          medicalConditionId: '',
                          severity: hasImmunologySpecialty ? 'moderate' : '',
                          locations: [],
                        })
                      }
                    >
                      <Body1>
                        {hasImmunologySpecialty
                          ? 'Add Secondary Indication'
                          : 'Add Secondary Diagnosis'}
                      </Body1>
                    </Link>
                    <Link
                      component="button"
                      type="button"
                      underline="hover"
                      onClick={() => {
                        history.push({
                          pathname: PatientSelfOnboardUrls.addTreatment.create(
                            patientId || '',
                            caseId || '',
                          ),
                          state: { from: location.pathname },
                        })
                      }}
                    >
                      <Body1>Skip and complete later</Body1>
                    </Link>
                  </MuiStack>
                </>
              )
            }}
          </FieldArray>
        </FormikProvider>
      </form>
      <MuiStack
        direction="row"
        justifyContent="center"
        sx={{ marginTop: '40px', paddingBottom: '40px' }}
      >
        <Button
          className={classes.goBackButton}
          disableElevation
          fullWidth
          style={{ marginRight: '20px' }}
          onClick={history.goBack}
        >
          Go Back
        </Button>
        {/* TODO: change submit form to validation and change step */}
        <Button
          variant="contained"
          disableElevation
          fullWidth
          disabled={!isValid}
          loading={isSubmitting}
          onClick={() => submitForm()}
        >
          Continue
        </Button>
      </MuiStack>
    </Box>
  )
}

export default Diagnosis
